import type {
  AddBlocklistItemPayload,
  BlocklistItem,
} from '@pin/schemas/blocklist';
import {
  catchError,
  firstValueFrom,
  NEVER,
  repeat,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  timeout,
  timer,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { withIdToken } from '../firebase/auth';
import { isVisible$ } from '../utils/rxjs.dom';
import {
  checkNoContentSuccess,
  HttpError,
  retryWithBackoff,
} from '../utils/rxjs.http';

export type { BlocklistItem };

const fetchBlocklistItems$ = withIdToken((token) =>
  fromFetch(
    `${import.meta.env.VITE_ADMIN_API_URL}/admin/blocklist-dynamic/items`,
    {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      selector: (r): Promise<BlocklistItem[]> => {
        if (!r.ok) {
          throw new HttpError(r);
        }
        return r.json();
      },
    }
  ).pipe(timeout(6_000), retryWithBackoff(3))
);

export const blocklistSubject$ = new Subject<void>();
export const pollBlocklistItems = (errorDelay = 5_000, repeatDelay = 2_500) =>
  isVisible$.pipe(
    switchMap((visible) => {
      if (!visible) {
        return NEVER;
      }
      return blocklistSubject$.pipe(
        startWith(0),
        switchMap(() =>
          fetchBlocklistItems$.pipe(
            catchError((_err: unknown, source) =>
              timer(errorDelay).pipe(switchMap(() => source))
            ),
            repeat({ delay: () => timer(repeatDelay) })
          )
        )
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

export const addBlocklistItem = async (payload: AddBlocklistItemPayload) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/blocklist-dynamic/items`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};

export const deleteBlocklistItem = async (itemId: string) => {
  await firstValueFrom(
    withIdToken((token) =>
      fromFetch(
        `${import.meta.env.VITE_ADMIN_API_URL}/admin/blocklist-dynamic/items/${encodeURIComponent(itemId)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          method: 'DELETE',
        }
      ).pipe(checkNoContentSuccess(), timeout(6_000), retryWithBackoff(4))
    )
  );
};
